import { useState, useEffect } from 'react';
import { apiFetch } from './api';

const HULL_SIZES = [
  { value: '8', label: 'Eight' },
  { value: '4', label: 'Four' },
  { value: '2', label: 'Pair' },
];

export default function BoatConfigSettings() {
  const [config, setConfig] = useState({ hulls: [], oars: [], coxBoxes: [], launches: [], speedCoaches: [], coaches: [] });
  const [teams, setTeams] = useState([]);
  const [squads, setSquads] = useState([]);
  const [assetStatuses, setAssetStatuses] = useState([]);
  const [newValue, setNewValue] = useState('');
  const [newHullSize, setNewHullSize] = useState(HULL_SIZES[0].value);
  const [newHullRig, setNewHullRig] = useState('');
  const [selectedType, setSelectedType] = useState('hull');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('hulls');
  const [showHint, setShowHint] = useState(false);
  const [newLaunchLocation, setNewLaunchLocation] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterSquad, setFilterSquad] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  async function loadConfig() {
    try {
      setLoading(true);
      setError('');
      const [configData, teamsData, squadsData, statusData] = await Promise.all([
        apiFetch('/boat-config'),
        apiFetch('/teams'),
        apiFetch('/squads'),
        apiFetch('/asset-status')
      ]);
      setConfig({
        hulls: configData.hulls || [],
        oars: configData.oars || [],
        coxBoxes: configData.coxBoxes || [],
        launches: configData.launches || [],
        speedCoaches: configData.speedCoaches || [],
        coaches: configData.coaches || []
      });
      setTeams(teamsData);
      setSquads(squadsData);
      setAssetStatuses(statusData);
    } catch (e) {
      setError(e.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadConfig();
  }, []);
  useEffect(() => {
    if (activeTab === 'hulls') {
      setSelectedType('hull');
    } else if (activeTab === 'oars') {
      setSelectedType('oars');
    } else if (activeTab === 'cox_boxes') {
  setSelectedType('cox_Boxes');
    } else if (activeTab === 'launches') {
      setSelectedType('launch');
    } else if (activeTab === 'speed_coaches') {
      setSelectedType('speed_coach');
    }
  }, [activeTab]);

  async function addOption() {
    if (!newValue.trim()) {
      alert('Please enter a value');
      return;
    }

    try {
      await apiFetch('/boat-config', {
        method: 'POST',
        body: JSON.stringify({
          configType: selectedType,
          value: newValue.trim(),
          size: selectedType === 'hull' ? newHullSize : undefined,
          rig: selectedType === 'hull' ? newHullRig.trim() : undefined,
          location: selectedType === 'launch' ? newLaunchLocation.trim() : undefined
        })
      });
      setNewValue('');
      if (selectedType === 'hull') {
        setNewHullSize(HULL_SIZES[0].value);
        setNewHullRig('');
      }
      if (selectedType === 'launch') {
        setNewLaunchLocation('');
      }
      await loadConfig();
    } catch (e) {
      alert(e.message || 'Failed to add option');
    }
  }

  async function updateHullSize(id, size) {
    try {
      await apiFetch(`/boat-config/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ size })
      });
      await loadConfig();
    } catch (e) {
      alert(e.message || 'Failed to update hull size');
    }
  }

  async function updateAssetProperty(id, property, value) {
    try {
      let normalizedValue = value;
      if (typeof normalizedValue === 'string') {
        normalizedValue = normalizedValue.trim();
      }
      if (normalizedValue === '') {
        normalizedValue = null;
      }
      await apiFetch(`/boat-config/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [property]: normalizedValue })
      });
      await loadConfig();
    } catch (e) {
      alert(e.message || `Failed to update ${property}`);
    }
  }

  const updateLocalConfigField = (listKey, id, property, value) => {
    setConfig((prev) => {
      const list = Array.isArray(prev[listKey]) ? prev[listKey] : [];
      return {
        ...prev,
        [listKey]: list.map((item) => (item.id === id ? { ...item, [property]: value } : item))
      };
    });
  };

  async function deleteOption(id, label) {
    const confirmed = window.confirm(`Delete this ${label}?`);
    if (!confirmed) return;
    try {
      await apiFetch(`/boat-config/${id}`, {
        method: 'DELETE'
      });
      await loadConfig();
    } catch (e) {
      alert(e.message || `Failed to delete ${label}`);
    }
  }
const applyFilters = (list) => {
  return list.filter((item) => {
    if (filterTeam && String(item.teamId || '') !== filterTeam) return false;
    if (filterSquad && String(item.squadId || '') !== filterSquad) return false;
    if (filterStatus && String(item.statusId || '') !== filterStatus) return false;
    return true;
  });
};
const getCurrentList = () => {
  switch (activeTab) {
    case 'hulls':
      return applyFilters(config.hulls);
    case 'oars':
      return applyFilters(config.oars);
    case 'cox_boxes':
      return applyFilters(config.coxBoxes);
    case 'launches':
      return applyFilters(config.launches);
    case 'speed_coaches':
      return applyFilters(config.speedCoaches);
    case 'coaches':
      return config.coaches.map((name, idx) => ({ id: idx, value: name }));
    default:
      return [];
  }
};
const currentList = getCurrentList();
  return (
    <div className="card boat-config-card-compact">
      <div className="boat-config-header-compact">
        <div>
          <h2 className="h2" style={{ marginTop: 0, marginBottom: 4 }}>Asset Configuration</h2>
          <p className="help" style={{ margin: 0 }}>
            Manage dropdown options for hulls, oars, and cox boxes
          </p>
        </div>
        {loading && <span className="help">Loading…</span>}
        {error && <span className="help boat-config-error">{error}</span>}
      </div>

      <div className="boat-config-form-compact">
        <select
          className="input"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={{ minWidth: 80 }}
        >
          <option value="hull">Hull</option>
          <option value="oars">Oars</option>
          <option value="cox_boxes">Cox Boxes</option>
          <option value="launch">Launches</option>
          <option value="speed_coach">Speed Coaches</option>
        </select>

        {selectedType === 'hull' && (
          <select
            className="input"
            value={newHullSize}
            onChange={(e) => setNewHullSize(e.target.value)}
            style={{ minWidth: 90 }}
          >
            {HULL_SIZES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        )}

        {selectedType === 'hull' && (
          <input
            className="input"
            value={newHullRig}
            onChange={(e) => setNewHullRig(e.target.value)}
            placeholder="Rig"
            style={{ minWidth: 120 }}
          />
        )}

        <input
          className="input"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={
            selectedType === 'hull' ? 'e.g., Vespoli, Empacher' :
            selectedType === 'oars' ? 'e.g., Concept2, Croker' :
            selectedType === 'launch' ? 'e.g., Launch name' :
            selectedType === 'cox_boxes' ? 'e.g., GPS1' :
            'e.g., NK, Coxmate'
            
          }
          onKeyDown={(e) => e.key === 'Enter' && addOption()}
          style={{ flex: 1, minWidth: 150 }}
        />

        {selectedType === 'launch' && (
          <input
            className="input"
            value={newLaunchLocation}
            onChange={(e) => setNewLaunchLocation(e.target.value)}
            placeholder="Location"
            style={{ minWidth: 150 }}
          />
        )}

        <button className="btn" onClick={addOption} style={{ whiteSpace: 'nowrap' }}>
          + Add
        </button>
      </div>

      <div className="boat-config-tabs">
        <button
          className={`boat-config-tab ${activeTab === 'hulls' ? 'active' : ''}`}
          onClick={() => setActiveTab('hulls')}
        >
          Hulls ({config.hulls.length})
        </button>
        <button
          className={`boat-config-tab ${activeTab === 'oars' ? 'active' : ''}`}
          onClick={() => setActiveTab('oars')}
        >
          Oars ({config.oars.length})
        </button>
        <button
          className={`boat-config-tab ${activeTab === 'cox_boxes' ? 'active' : ''}`}
          onClick={() => setActiveTab('cox_boxes')}
        >
          Cox Boxes ({config.coxBoxes.length})
        </button>
        <button
          className={`boat-config-tab ${activeTab === 'launches' ? 'active' : ''}`}
          onClick={() => setActiveTab('launches')}
        >
          Launches ({config.launches.length})
        </button>
        <button
          className={`boat-config-tab ${activeTab === 'speed_coaches' ? 'active' : ''}`}
          onClick={() => setActiveTab('speed_coaches')}
        >
          Speed Coaches ({config.speedCoaches.length})
        </button>
        <button
          className={`boat-config-tab ${activeTab === 'coaches' ? 'active' : ''}`}
          onClick={() => setActiveTab('coaches')}
        >
          Coaches ({config.coaches.length})
        </button>
      </div>
{/* Filters */}
{activeTab !== 'coaches' && (
  <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
    <select
      className="input"
      value={filterTeam}
      onChange={(e) => setFilterTeam(e.target.value)}
      style={{ maxWidth: 180 }}
    >
      <option value="">All Teams</option>
      {teams.map((team) => (
        <option key={team.id} value={team.id}>{team.name}</option>
      ))}
    </select>

    <select
      className="input"
      value={filterSquad}
      onChange={(e) => setFilterSquad(e.target.value)}
      style={{ maxWidth: 180 }}
    >
      <option value="">All Squads</option>
      {squads.map((squad) => (
        <option key={squad.id} value={squad.id}>{squad.name}</option>
      ))}
    </select>

    <select
      className="input"
      value={filterStatus}
      onChange={(e) => setFilterStatus(e.target.value)}
      style={{ maxWidth: 180 }}
    >
      <option value="">All Statuses</option>
      {assetStatuses.map((status) => (
        <option key={status.id} value={status.id}>{status.name}</option>
      ))}
    </select>
    <button
  className="btn ghost small"
  onClick={() => {
    setFilterTeam('');
    setFilterSquad('');
    setFilterStatus('');
  }}
  style={{ whiteSpace: 'nowrap' }}
>
  Clear Filters
</button>
  </div>
)}
  
      <div className="boat-config-tab-content">
        {currentList.length === 0 ? (
          <p className="help" style={{ textAlign: 'center', padding: 16 }}>
            {activeTab === 'coaches'
              ? 'No coaches found. Coaches are auto-generated from users with coach role.'
              : `No ${activeTab} configured yet. Add some above!`}
          </p>
        ) : (
          <ul className="list" style={{ margin: 0 }}>
                {activeTab === 'hulls' && currentList.map((hull) => (
              <li key={hull.id} className="boat-config-item-compact" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div className="boat-config-item-info">
                    <strong>{hull.value}</strong>
                    
                  </div>
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => deleteOption(hull.id, 'hull')}
                    title="Delete hull"
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                  <select
                    className="input-compact"
                    value={hull.size || ''}
                    onChange={(e) => updateHullSize(hull.id, e.target.value || null)}
                    title="Size"
                  >
                    <option value="">Size: All</option>
                    {HULL_SIZES.map((option) => (
                      <option key={option.value} value={option.value}>Size: {option.label}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={hull.teamId || ''}
                    onChange={(e) => updateAssetProperty(hull.id, 'teamId', e.target.value)}
                    title="Team"
                  >
                    <option value="">Team: None</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>Team: {team.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={hull.squadId || ''}
                    onChange={(e) => updateAssetProperty(hull.id, 'squadId', e.target.value)}
                    title="Squad"
                  >
                    <option value="">Squad: None</option>
                    {squads.map((squad) => (
                      <option key={squad.id} value={squad.id}>Squad: {squad.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={hull.statusId || ''}
                    onChange={(e) => updateAssetProperty(hull.id, 'statusId', e.target.value)}
                    title="Status"
                  >
                    <option value="">Status: None</option>
                    {assetStatuses.map((status) => (
                      <option key={status.id} value={status.id}>Status: {status.name}</option>
                    ))}
                  </select>
                  <input
                    className="input-compact"
                    value={hull.spread || ''}
                    placeholder="Spread"
                    onChange={(e) => updateLocalConfigField('hulls', hull.id, 'spread', e.target.value)}
                    onBlur={(e) => updateAssetProperty(hull.id, 'spread', e.target.value)}
                    title="Spread"
                  />
                  <input
                    className="input-compact"
                    value={hull.rig || ''}
                    placeholder="Rig"
                    onChange={(e) => updateLocalConfigField('hulls', hull.id, 'rig', e.target.value)}
                    onBlur={(e) => updateAssetProperty(hull.id, 'rig', e.target.value)}
                    title="Rig"
                  />
                </div>
              </li>
            ))}

               {activeTab === 'oars' && currentList.map((oar) => (
              <li key={oar.id} className="boat-config-item-compact" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong>{oar.value}</strong>
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => deleteOption(oar.id, 'oars option')}
                    title="Delete oars"
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                  <select
                    className="input-compact"
                    value={oar.teamId || ''}
                    onChange={(e) => updateAssetProperty(oar.id, 'teamId', e.target.value)}
                    title="Team"
                  >
                    <option value="">Team: None</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>Team: {team.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={oar.squadId || ''}
                    onChange={(e) => updateAssetProperty(oar.id, 'squadId', e.target.value)}
                    title="Squad"
                  >
                    <option value="">Squad: None</option>
                    {squads.map((squad) => (
                      <option key={squad.id} value={squad.id}>Squad: {squad.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={oar.statusId || ''}
                    onChange={(e) => updateAssetProperty(oar.id, 'statusId', e.target.value)}
                    title="Status"
                  >
                    <option value="">Status: None</option>
                    {assetStatuses.map((status) => (
                      <option key={status.id} value={status.id}>Status: {status.name}</option>
                    ))}
                  </select>
                  <input
                    className="input-compact"
                    value={oar.oarLength || ''}
                    placeholder="Length"
                    onChange={(e) => updateLocalConfigField('oars', oar.id, 'oarLength', e.target.value)}
                    onBlur={(e) => updateAssetProperty(oar.id, 'oarLength', e.target.value)}
                    title="Length"
                  />
                  <input
                    className="input-compact"
                    value={oar.oarInboard || ''}
                    placeholder="Inboard"
                    onChange={(e) => updateLocalConfigField('oars', oar.id, 'oarInboard', e.target.value)}
                    onBlur={(e) => updateAssetProperty(oar.id, 'oarInboard', e.target.value)}
                    title="Inboard"
                  />
                </div>
              </li>
            ))}

            {activeTab === 'cox_boxes' && currentList.map((coxBox) => (
              <li key={coxBox.id} className="boat-config-item-compact" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong>{coxBox.value}</strong>
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => deleteOption(coxBox.id, 'cox box option')}
                    title="Delete cox box"
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                  <select
                    className="input-compact"
                    value={coxBox.teamId || ''}
                    onChange={(e) => updateAssetProperty(coxBox.id, 'teamId', e.target.value)}
                    title="Team"
                  >
                    <option value="">Team: None</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>Team: {team.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={coxBox.squadId || ''}
                    onChange={(e) => updateAssetProperty(coxBox.id, 'squadId', e.target.value)}
                    title="Squad"
                  >
                    <option value="">Squad: None</option>
                    {squads.map((squad) => (
                      <option key={squad.id} value={squad.id}>Squad: {squad.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={coxBox.statusId || ''}
                    onChange={(e) => updateAssetProperty(coxBox.id, 'statusId', e.target.value)}
                    title="Status"
                  >
                    <option value="">Status: None</option>
                    {assetStatuses.map((status) => (
                      <option key={status.id} value={status.id}>Status: {status.name}</option>
                    ))}
                  </select>
                </div>
              </li>
            ))}

            {activeTab === 'speed_coaches' && currentList.map((speedCoach) => (
              <li key={speedCoach.id} className="boat-config-item-compact" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong>{speedCoach.value}</strong>
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => deleteOption(speedCoach.id, 'speed coach option')}
                    title="Delete speed coach"
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                  <select
                    className="input-compact"
                    value={speedCoach.teamId || ''}
                    onChange={(e) => updateAssetProperty(speedCoach.id, 'teamId', e.target.value)}
                    title="Team"
                  >
                    <option value="">Team: None</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>Team: {team.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={speedCoach.squadId || ''}
                    onChange={(e) => updateAssetProperty(speedCoach.id, 'squadId', e.target.value)}
                    title="Squad"
                  >
                    <option value="">Squad: None</option>
                    {squads.map((squad) => (
                      <option key={squad.id} value={squad.id}>Squad: {squad.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={speedCoach.statusId || ''}
                    onChange={(e) => updateAssetProperty(speedCoach.id, 'statusId', e.target.value)}
                    title="Status"
                  >
                    <option value="">Status: None</option>
                    {assetStatuses.map((status) => (
                      <option key={status.id} value={status.id}>Status: {status.name}</option>
                    ))}
                  </select>
                </div>
              </li>
            ))}

            {activeTab === 'launches' && currentList.map((launch) => (
              <li key={launch.id} className="boat-config-item-compact" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong>{launch.value}</strong>
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => deleteOption(launch.id, 'launch option')}
                    title="Delete launch"
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                  <input
                    className="input-compact"
                    value={launch.location || ''}
                    placeholder="Location"
                    onChange={(e) => updateLocalConfigField('launches', launch.id, 'location', e.target.value)}
                    onBlur={(e) => updateAssetProperty(launch.id, 'location', e.target.value)}
                    title="Location"
                  />
                  <select
                    className="input-compact"
                    value={launch.teamId || ''}
                    onChange={(e) => updateAssetProperty(launch.id, 'teamId', e.target.value)}
                    title="Team"
                  >
                    <option value="">Team: None</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>Team: {team.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={launch.squadId || ''}
                    onChange={(e) => updateAssetProperty(launch.id, 'squadId', e.target.value)}
                    title="Squad"
                  >
                    <option value="">Squad: None</option>
                    {squads.map((squad) => (
                      <option key={squad.id} value={squad.id}>Squad: {squad.name}</option>
                    ))}
                  </select>
                  <select
                    className="input-compact"
                    value={launch.statusId || ''}
                    onChange={(e) => updateAssetProperty(launch.id, 'statusId', e.target.value)}
                    title="Status"
                  >
                    <option value="">Status: None</option>
                    {assetStatuses.map((status) => (
                      <option key={status.id} value={status.id}>Status: {status.name}</option>
                    ))}
                  </select>
                </div>
              </li>
            ))}

            {activeTab === 'coaches' && config.coaches.map((c, idx) => (
              <li key={idx} className="boat-config-item-compact">
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="boat-config-hint-compact">
        <button
          className="boat-config-hint-toggle"
          onClick={() => setShowHint(!showHint)}
        >
          💡 Tips {showHint ? '▼' : '▶'}
        </button>
        {showHint && (
          <div className="boat-config-hint-content">
            <p className="help" style={{ margin: '0 0 6px 0' }}>
              These options appear as dropdowns when coaches build boats.
            </p>
            <p className="help" style={{ margin: 0 }}>
              <strong>Examples:</strong> Hulls: Vespoli, Empacher, Hudson • Oars: Concept2, Croker, Comp
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
